import { type FC, useRef, useEffect, useState } from 'react';
import { graphql, useFragment, usePaginationFragment } from 'react-relay';

import { flattenConnectionNodes } from '../../utils/relayUtils.ts';
import { PdpRespSimilarItemsGrid } from './PdpRespSimilarItemsGrid.tsx';
import { youMayAlsoLike } from '../../shared/sharedMessages.jsx';
import { trackSimilarItemImpressions } from '../tracking/similarItemsTracking.js';

import classnames from 'classnames';
import styles from './PdpRespRefetchableSimilarItemsGrid.scss';
import dibsCss from 'dibs-css';

import { type Props } from './PdpRespSimilarItemsGrid.tsx';
import { type PdpRespRefetchableSimilarItemsGrid_viewer$key as Viewer } from './__generated__/PdpRespRefetchableSimilarItemsGrid_viewer.graphql.ts';
import { type PdpRespRefetchableSimilarItemsGrid_user$key as User } from './__generated__/PdpRespRefetchableSimilarItemsGrid_user.graphql.ts';
import { type PdpRespRefetchableSimilarItemsGrid_item$key as Item } from './__generated__/PdpRespRefetchableSimilarItemsGrid_item.graphql.ts';

const viewerFragment = graphql`
    fragment PdpRespRefetchableSimilarItemsGrid_viewer on Viewer
    @argumentDefinitions(
        first: { type: "Int", defaultValue: 10 }
        after: { type: "String", defaultValue: "" }
        itemId: { type: "String", defaultValue: "" }
    )
    @refetchable(queryName: "PdpRespRefetchableSimilarItemsGrid_viewerRefetchQuery") {
        ...PdpRespSimilarItemsGrid_viewer
        moreLikeThisItem(
            first: $first
            after: $after
            seller: EXCLUDE
            itemId: $itemId
            useML: $isSimilarityEndpointTestVariant
            maxMLItems: 40
        ) @connection(key: "PdpRespRefetchableSimilarItemsGrid_moreLikeThisItem") {
            # edges used in flattenConnectionNodes
            # eslint-disable-next-line relay/unused-fields
            edges {
                node {
                    serviceId
                    title
                    browseUrl
                    categoryCode
                    ecommerceTrackingParams
                    ...PdpRespSimilarItemsGrid_similarItems
                }
            }
            totalResults
            pageInfo {
                hasNextPage
                hasPreviousPage
                startCursor
                endCursor
            }
        }
    }
`;
const itemFragment = graphql`
    fragment PdpRespRefetchableSimilarItemsGrid_item on Item {
        ...PdpRespSimilarItemsGrid_item
    }
`;
const userFragment = graphql`
    fragment PdpRespRefetchableSimilarItemsGrid_user on User {
        ...PdpRespSimilarItemsGrid_user
    }
`;

type PdpRespRefetchableSimilarItemsGridProps = Omit<
    Props,
    'similarItems' | 'user' | 'viewer' | 'item'
> & {
    viewer: Viewer;
    user?: User | null;
    item: Item;
};

const MAXIMUM_ITEMS_COUNT = 40;
const MORE_ITEMS_FETCH_COUNT = 15;
const ITEMS_PER_ROW = 5;

export const PdpRespRefetchableSimilarItemsGrid: FC<PdpRespRefetchableSimilarItemsGridProps> = ({
    viewer: viewerRef,
    user: userRef,
    item: itemRef,
    currency,
    favoritesLocation,
    gridLocation,
    dataTn,
    relayVariables,
    gridItemWrapperClass,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const impressionTrackingRef = useRef<Record<string, boolean>>({});

    const [shouldLoadUntilScrollDepthReached, setShouldLoadUntilScrollDepthReached] =
        useState(false);

    const {
        data: viewer,
        loadNext,
        isLoadingNext,
        hasNext,
    } = usePaginationFragment(viewerFragment, viewerRef);

    const maxItemsReached =
        viewer?.moreLikeThisItem?.edges &&
        viewer.moreLikeThisItem.edges.length >= MAXIMUM_ITEMS_COUNT;
    const { totalResults } = viewer?.moreLikeThisItem || {};

    const itemsToShow = flattenConnectionNodes(viewer?.moreLikeThisItem) || [];
    const placeholdersCount =
        (totalResults && totalResults < MAXIMUM_ITEMS_COUNT ? totalResults : MAXIMUM_ITEMS_COUNT) -
        itemsToShow.length;

    useEffect(() => {
        const remainingItemsCount = MAXIMUM_ITEMS_COUNT - itemsToShow.length;

        if (
            shouldLoadUntilScrollDepthReached &&
            !maxItemsReached &&
            !isLoadingNext &&
            hasNext &&
            remainingItemsCount
        ) {
            loadNext(remainingItemsCount);
        }
    }, [
        hasNext,
        isLoadingNext,
        itemsToShow.length,
        loadNext,
        maxItemsReached,
        shouldLoadUntilScrollDepthReached,
    ]);

    function loadMore({ disconnect }: { disconnect?: () => void }): void {
        if (isLoadingNext || !hasNext) {
            return;
        }

        if (maxItemsReached) {
            disconnect?.();
            return;
        }

        loadNext(MORE_ITEMS_FETCH_COUNT);
    }

    function trackItemsImpression({ index }: { index: number }): void {
        const itemsToTrack = itemsToShow
            .slice(index, index + ITEMS_PER_ROW)
            .filter(i => (i?.serviceId ? !impressionTrackingRef.current[i.serviceId] : false));

        itemsToTrack.forEach(i => {
            if (i?.serviceId) {
                impressionTrackingRef.current[i?.serviceId] = true;
            }
        });

        if (itemsToTrack.length) {
            trackSimilarItemImpressions(itemsToTrack, gridLocation, index);
        }
    }

    const itemsPlaceholder = (
        <div className="rowFlex">
            {Array.from({ length: placeholdersCount }).map((_, index) => (
                <div
                    key={index}
                    data-tn={`similar-item-placeholder-${index}`}
                    className={classnames(
                        'colLg3 colMd6 colXs6',
                        styles.similarItem,
                        styles.placeholderItem,
                        dibsCss.pSmall
                    )}
                >
                    <div className={classnames(styles.imagePlaceholder, dibsCss.mbSmaller)} />
                    <div className={classnames(styles.titlePlaceholder, dibsCss.mbXsmall)} />
                    <div className={styles.pricePlaceholder} />
                </div>
            ))}
        </div>
    );

    return (
        <div>
            <PdpRespSimilarItemsGrid
                isUnavailablePdp
                header={youMayAlsoLike}
                relayVariables={relayVariables}
                viewer={viewer}
                currency={currency}
                user={user}
                item={item}
                similarItems={itemsToShow}
                favoritesLocation={favoritesLocation}
                gridLocation={gridLocation}
                dataTn={dataTn}
                isDark={false}
                hideBar
                gridItemWrapperClass={gridItemWrapperClass}
                onRowImpression={trackItemsImpression}
                onLastItemVisibilityChange={({ isVisible, boundingClientRect, disconnect }) => {
                    setShouldLoadUntilScrollDepthReached(
                        window && window.scrollY >= boundingClientRect.bottom
                    );

                    if (isVisible) {
                        loadMore({ disconnect });
                    }
                }}
            />
            {itemsPlaceholder}
        </div>
    );
};
